import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiSearch, FiX, FiArrowDown, FiWifiOff } from "react-icons/fi";
import { useRoomMessages } from "../../../context/RoomMessagesContext";
import useJumpToMessage from "../../../hooks/useJumpToMessage";
import MessageComponent from "./chat/MessageComponent";
import MessageInput from "./chat/MessageInput";
import PinnedMessagesBar from "./chat/PinnedMessagesBar";
import LoadingBar from "../../ui/LoadingBar";
import "./ChatTab.css";

// ==============================================
// Helper Functions
// ==============================================
const getInitials = (name, username) => {
  if (name) {
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }
  if (username) {
    return username.substring(0, 2).toUpperCase();
  }
  return "??";
};

const getAvatarColor = (name) => {
  const colors = [
    "#3b82f6",
    "#8b5cf6",
    "#10b981",
    "#f59e0b",
    "#ec4899",
    "#06b6d4",
    "#6366f1",
    "#14b8a6",
  ];
  const index = (name || "").charCodeAt(0) % colors.length;
  return colors[index];
};

// ==============================================
// Animation Variants
// ==============================================
const fadeVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.2 } },
  exit: { opacity: 0, transition: { duration: 0.15 } },
};

const slideUpVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.2 } },
  exit: { opacity: 0, y: -10, transition: { duration: 0.15 } },
};

const searchVariants = {
  hidden: { opacity: 0, y: -10, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: "spring", damping: 25, stiffness: 400 },
  },
  exit: { opacity: 0, y: -10, scale: 0.95, transition: { duration: 0.15 } },
};

// ==============================================
// Sub-Components
// ==============================================

// Connection Status Banner
const ConnectionStatus = ({ isConnected, show }) => {
  if (!show || isConnected) return null;

  return (
    <motion.div
      className="rchat-connection"
      variants={fadeVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
    >
      <div className="rchat-connection-icon">
        <FiWifiOff />
      </div>
      <span>Connecting for real-time updates...</span>
      <div className="rchat-connection-pulse" />
    </motion.div>
  );
};

// Jump Mode Banner
const JumpModeBanner = ({ onJumpToLatest }) => (
  <motion.div
    className="rchat-jump-banner"
    variants={slideUpVariants}
    initial="hidden"
    animate="visible"
    exit="exit"
  >
    <span className="rchat-jump-text">
      <span className="rchat-jump-icon">📍</span>
      Viewing older messages
    </span>
    <motion.button
      className="rchat-jump-btn"
      onClick={onJumpToLatest}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      <FiArrowDown />
      <span>Jump to latest</span>
    </motion.button>
  </motion.div>
);

// Search Button
const SearchButton = ({ isOpen, onClick }) => (
  <motion.button
    className={`rchat-search-btn ${isOpen ? "rchat-search-btn--active" : ""}`}
    onClick={onClick}
    whileHover={{ scale: 1.05 }}
    whileTap={{ scale: 0.95 }}
    title="Search messages"
  >
    {isOpen ? <FiX /> : <FiSearch />}
  </motion.button>
);

// Search Panel
const SearchPanel = ({
  isOpen,
  query,
  onQueryChange,
  onSearch,
  onClear,
  onClose,
  results,
  searching,
  onJumpToMessage,
}) => (
  <AnimatePresence>
    {isOpen && (
      <motion.div
        className="rchat-search-panel"
        variants={searchVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
      >
        <div className="rchat-search-box">
          <FiSearch className="rchat-search-icon" />
          <input
            type="text"
            className="rchat-search-input"
            placeholder="Search messages..."
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && onSearch(e)}
            autoFocus
          />
          {query && (
            <motion.button
              className="rchat-search-clear"
              onClick={onClear}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <FiX />
            </motion.button>
          )}
          <motion.button
            className="rchat-search-close"
            onClick={onClose}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <FiX />
          </motion.button>
        </div>

        {query && (
          <motion.div
            className="rchat-search-results"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
          >
            {searching ? (
              <div className="rchat-search-status">
                <div className="rchat-search-spinner" />
                <span>Searching...</span>
              </div>
            ) : results === null ? (
              <div className="rchat-search-status">
                <span>Press Enter to search</span>
              </div>
            ) : results.length === 0 ? (
              <div className="rchat-search-status">
                <span>No messages found</span>
              </div>
            ) : (
              <div className="rchat-search-list">
                {results.map((msg) => (
                  <motion.div
                    key={msg._id}
                    className="rchat-search-item"
                    onClick={() => onJumpToMessage(msg._id)}
                    whileHover={{
                      x: 4,
                      backgroundColor: "rgba(59, 130, 246, 0.08)",
                    }}
                  >
                    {msg.sender?.profilePhotoUrl ? (
                      <img
                        src={msg.sender.profilePhotoUrl}
                        alt={msg.sender?.name || "User"}
                        className="rchat-search-avatar"
                      />
                    ) : (
                      <div
                        className="rchat-search-avatar rchat-search-avatar--initials"
                        style={{
                          backgroundColor: getAvatarColor(
                            msg.sender?.name || msg.sender?.username
                          ),
                        }}
                      >
                        {getInitials(msg.sender?.name, msg.sender?.username)}
                      </div>
                    )}
                    <div className="rchat-search-content">
                      <div className="rchat-search-header">
                        <span className="rchat-search-name">
                          {msg.sender?.name ||
                            msg.sender?.username ||
                            "Unknown User"}
                        </span>
                        <span className="rchat-search-time">
                          {new Date(msg.createdAt).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="rchat-search-text">
                        {msg.content?.substring(0, 120)}
                        {msg.content?.length > 120 ? "..." : ""}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </motion.div>
    )}
  </AnimatePresence>
);

// Typing Indicator
const TypingIndicator = ({ typingUsers }) => {
  if (typingUsers.length === 0) return null;

  const getTypingText = () => {
    const names = typingUsers.map((u) => u.name || u.username || "Someone");
    if (names.length === 1) return `${names[0]} is typing...`;
    if (names.length === 2) return `${names[0]} and ${names[1]} are typing...`;
    return `${names[0]} and ${names.length - 1} others are typing...`;
  };

  return (
    <motion.div
      className="rchat-typing"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 10 }}
    >
      <div className="rchat-typing-dots">
        <span />
        <span />
        <span />
      </div>
      <span className="rchat-typing-text">{getTypingText()}</span>
    </motion.div>
  );
};

// Scroll Status
const ScrollStatus = ({ type, text }) => (
  <div className={`rchat-scroll-status rchat-scroll-status--${type}`}>
    <span>{text}</span>
  </div>
);

// Empty State
const EmptyState = () => (
  <motion.div
    className="rchat-empty"
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
  >
    <div className="rchat-empty-icon">💬</div>
    <h3>No messages yet</h3>
    <p>Start the conversation!</p>
  </motion.div>
);

// ==============================================
// Main Component
// ==============================================
const ChatTab = ({ roomId, userRole, roomMembers = [] }) => {
  const {
    messages,
    loading,
    hasMore,
    typingUsers,
    socketConnected,
    joinRoom,
    leaveRoom,
    loadMessages,
    searchMessages,
    markAsRead,
  } = useRoomMessages();

  // Jump to message hook
  const {
    jumpedMessages,
    targetMessageId,
    hasOlder: jumpHasOlder,
    hasNewer: jumpHasNewer,
    isJumpedMode,
    isJumping,
    jumpToMessage,
    loadOlder: jumpLoadOlder,
    loadNewer: jumpLoadNewer,
    exitJumpedMode,
    clearHighlight,
  } = useJumpToMessage(roomId);

  // UI State
  const [replyTo, setReplyTo] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [showConnectionStatus, setShowConnectionStatus] = useState(false);

  // Refs
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const targetMessageRef = useRef(null);
  const pendingReadIds = useRef(new Set());
  const markAsReadTimeout = useRef(null);
  const wasConnectedRef = useRef(false);
  const isLoadingMoreRef = useRef(false);
  const hasMoreRef = useRef(true);
  const cooldownRef = useRef(false);

  const displayMessages = isJumpedMode ? jumpedMessages : messages;

  // Connection status logic
  useEffect(() => {
    if (socketConnected) {
      wasConnectedRef.current = true;
      setShowConnectionStatus(false);
      return;
    }

    if (wasConnectedRef.current && !socketConnected) {
      setShowConnectionStatus(true);
      return;
    }

    const timer = setTimeout(() => {
      if (!socketConnected) setShowConnectionStatus(true);
    }, 3000);

    return () => clearTimeout(timer);
  }, [socketConnected]);

  // Initialize chat
  useEffect(() => {
    if (roomId) {
      setInitialLoadComplete(false);
      setShowConnectionStatus(false);
      wasConnectedRef.current = false;
      exitJumpedMode();
      joinRoom(roomId);
    }

    return () => {
      if (roomId) {
        leaveRoom(roomId);
        setInitialLoadComplete(false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId]);

  // Mark initial load complete
  useEffect(() => {
    if (messages.length > 0 && !initialLoadComplete && !loading) {
      setTimeout(() => scrollToBottom(), 100);
      setTimeout(() => setInitialLoadComplete(true), 1000);
    }
  }, [messages.length, loading, initialLoadComplete]);

  // Auto-scroll on new messages
  useEffect(() => {
    if (!initialLoadComplete || isJumpedMode) return;

    if (messages.length > 0 && messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      const isNearBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight <
        150;
      if (isNearBottom) setTimeout(() => scrollToBottom(), 100);
    }
  }, [messages, initialLoadComplete, isJumpedMode]);

  // Auto-scroll for typing indicator
  useEffect(() => {
    if (!initialLoadComplete || isJumpedMode || typingUsers.length === 0)
      return;
    setTimeout(() => scrollToBottom(), 100);
  }, [typingUsers.length, initialLoadComplete, isJumpedMode]);

  // Scroll to target message
  useEffect(() => {
    if (targetMessageId && targetMessageRef.current) {
      setTimeout(() => {
        targetMessageRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 100);
      setTimeout(() => clearHighlight(), 3000);
    }
  }, [targetMessageId, clearHighlight]);

  // Keep hasMore in sync
  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  // Load more messages
  const loadMoreMessages = useCallback(async () => {
    if (isLoadingMoreRef.current || cooldownRef.current || !hasMoreRef.current)
      return false;

    const container = messagesContainerRef.current;
    if (!container) return false;

    isLoadingMoreRef.current = true;
    cooldownRef.current = true;
    setLoadingMore(true);

    const scrollHeightBefore = container.scrollHeight;

    try {
      await loadMessages(roomId, false);

      await new Promise((resolve) => {
        const observer = new MutationObserver(() => {
          observer.disconnect();
          resolve();
        });
        observer.observe(container, { childList: true, subtree: true });
        setTimeout(() => {
          observer.disconnect();
          resolve();
        }, 200);
      });

      const scrollHeightAfter = container.scrollHeight;
      const heightDiff = scrollHeightAfter - scrollHeightBefore;
      if (heightDiff > 0) container.scrollTop += heightDiff;

      return true;
    } catch (error) {
return false;
    } finally {
      setLoadingMore(false);
      isLoadingMoreRef.current = false;
      setTimeout(() => {
        cooldownRef.current = false;
      }, 800);
    }
  }, [roomId, loadMessages]);

  // Scroll handler
  useEffect(() => {
    if (!initialLoadComplete) return;

    const container = messagesContainerRef.current;
    if (!container) return;

    let scrollTimeout = null;

    const handleScroll = () => {
      if (scrollTimeout) clearTimeout(scrollTimeout);

      scrollTimeout = setTimeout(async () => {
        const { scrollTop, scrollHeight, clientHeight } = container;

        if (isJumpedMode) {
          if (scrollTop < 100 && jumpHasOlder) {
            const prevScrollHeight = scrollHeight;
            const count = await jumpLoadOlder();
            if (count) {
              requestAnimationFrame(() => {
                container.scrollTop = container.scrollHeight - prevScrollHeight;
              });
            }
          }
          if (scrollHeight - scrollTop - clientHeight < 100 && jumpHasNewer) {
            await jumpLoadNewer();
          }
        } else {
          if (
            scrollTop < 100 &&
            !isLoadingMoreRef.current &&
            !cooldownRef.current &&
            hasMoreRef.current
          ) {
            loadMoreMessages();
          }
        }
      }, 200);
    };

    container.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      container.removeEventListener("scroll", handleScroll);
      if (scrollTimeout) clearTimeout(scrollTimeout);
    };
  }, [
    initialLoadComplete,
    isJumpedMode,
    jumpHasOlder,
    jumpHasNewer,
    jumpLoadOlder,
    jumpLoadNewer,
    loadMoreMessages,
  ]);

  // Auto-mark as read
  useEffect(() => {
    if (!roomId || displayMessages.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries
          .filter((e) => e.isIntersecting)
          .forEach((e) =>
            pendingReadIds.current.add(e.target.dataset.messageId)
          );

        if (markAsReadTimeout.current) clearTimeout(markAsReadTimeout.current);

        markAsReadTimeout.current = setTimeout(() => {
          const idsToMark = Array.from(pendingReadIds.current).filter(Boolean);
          if (idsToMark.length > 0) {
            markAsRead(roomId, idsToMark);
            pendingReadIds.current.clear();
          }
        }, 1000);
      },
      { threshold: 0.5 }
    );

    document
      .querySelectorAll("[data-message-id]")
      .forEach((el) => observer.observe(el));

    return () => {
      observer.disconnect();
      if (markAsReadTimeout.current) clearTimeout(markAsReadTimeout.current);
    };
  }, [roomId, displayMessages.length, markAsRead]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (markAsReadTimeout.current) clearTimeout(markAsReadTimeout.current);
    };
  }, []);

  // Scroll to bottom
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Search handler
  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      setSearchResults(null);
      return;
    }

    setSearching(true);
    try {
      const results = await searchMessages(roomId, searchQuery, 20);
      setSearchResults(results?.data || []);
    } catch (error) {
setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  // Clear search
  const handleClearSearch = () => {
    setSearchQuery("");
    setSearchResults(null);
  };

  // Jump to message
  const handleJumpToMessage = async (messageId) => {
    handleClearSearch();
    setShowSearch(false);
    await jumpToMessage(messageId);
  };

  // Jump to latest
  const handleJumpToLatest = () => {
    exitJumpedMode();
    setTimeout(() => scrollToBottom(), 100);
  };

  // Loading states
  if (loading && messages.length === 0) {
    return <LoadingBar text="Loading messages..." />;
  }

  if (isJumping) {
    return <LoadingBar text="Jumping to message..." />;
  }

  return (
    <div className="rchat-container">
      {/* Connection Status */}
      <AnimatePresence>
        <ConnectionStatus
          isConnected={socketConnected}
          show={showConnectionStatus && messages.length > 0}
        />
      </AnimatePresence>

      {/* Pinned Messages */}
      <PinnedMessagesBar
        roomId={roomId}
        userRole={userRole}
        onJumpToMessage={handleJumpToMessage}
      />

      {/* Jump Mode Banner */}
      <AnimatePresence>
        {isJumpedMode && <JumpModeBanner onJumpToLatest={handleJumpToLatest} />}
      </AnimatePresence>

      {/* Search Header */}
      <div className="rchat-header">
        <SearchButton
          isOpen={showSearch}
          onClick={() => {
            setShowSearch(!showSearch);
            if (showSearch) handleClearSearch();
          }}
        />
        {/* Search Panel - positioned relative to header */}
        <SearchPanel
          isOpen={showSearch}
          query={searchQuery}
          onQueryChange={setSearchQuery}
          onSearch={handleSearch}
          onClear={handleClearSearch}
          onClose={() => {
            setShowSearch(false);
            handleClearSearch();
          }}
          results={searchResults}
          searching={searching}
          onJumpToMessage={handleJumpToMessage}
        />
      </div>

      {/* Messages Container */}
      <div className="rchat-messages" ref={messagesContainerRef}>
        {/* Load Status */}
        <div className="rchat-load-trigger">
          {loadingMore && (
            <ScrollStatus type="loading" text="Loading older messages..." />
          )}
          {isJumpedMode && jumpHasOlder && !loadingMore && (
            <ScrollStatus type="hint" text="↑ Scroll up for older messages" />
          )}
          {!isJumpedMode && !hasMore && messages.length > 0 && (
            <ScrollStatus type="end" text="Beginning of conversation" />
          )}
        </div>

        {/* Empty State */}
        {displayMessages.length === 0 && !loading && <EmptyState />}

        {/* Messages List */}
        <div className="rchat-list">
          {displayMessages.map((message) => {
            const isTarget = message._id === targetMessageId;
            return (
              <div
                key={message._id}
                ref={isTarget ? targetMessageRef : null}
                className={isTarget ? "rchat-highlight" : ""}
              >
                <MessageComponent
                  message={message}
                  roomId={roomId}
                  userRole={userRole}
                  onReply={() => setReplyTo(message)}
                  onJumpToMessage={handleJumpToMessage}
                  isHighlighted={isTarget}
                />
              </div>
            );
          })}

          {/* Typing Indicator */}
          <AnimatePresence>
            {typingUsers.length > 0 && !isJumpedMode && (
              <TypingIndicator typingUsers={typingUsers} />
            )}
          </AnimatePresence>

          {/* Scroll down hint */}
          {isJumpedMode && jumpHasNewer && (
            <ScrollStatus type="hint" text="↓ Scroll down for newer messages" />
          )}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Message Input */}
      <MessageInput
        roomId={roomId}
        replyTo={replyTo}
        onClearReply={() => setReplyTo(null)}
        roomMembers={roomMembers}
        isLeader={userRole?.isLeader || false}
      />
    </div>
  );
};

export default ChatTab;
